import { assign, assignWith } from "lodash";
export class ConfigurationManager {
    // input configs should be top-to-bottom, e.g. unit, investigation, problem
    constructor(defaults, configs) {
        Object.defineProperty(this, "defaults", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "configs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "warnedGroupStartFallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        this.defaults = defaults;
        // reverse the array so searches are bottom-to-top, e.g. problem, investigation, unit
        this.configs = [...configs].reverse();
    }
    getProp(prop) {
        var _a;
        const found = (_a = this.configs.find(config => config[prop] != null)) === null || _a === void 0 ? void 0 : _a[prop];
        return (found != null ? found : this.defaults[prop]);
    }
    /*
     * UnitConfiguration properties
     */
    get appName() {
        return this.getProp("appName");
    }
    get pageTitle() {
        return this.getProp("pageTitle");
    }
    get demoProblemTitle() {
        return this.getProp("demoProblemTitle");
    }
    get defaultProblemOrdinal() {
        return this.getProp("defaultProblemOrdinal");
    }
    get showCommentTag() {
        return this.getProp("showCommentTag");
    }
    get showCommentRating() {
        return this.getProp("showCommentRating");
    }
    get commentTags() {
        return (this.showCommentTag) ? this.getProp("commentTags") : {};
    }
    get allowCustomCommentTags() {
        return this.showCommentTag ? this.getProp("allowCustomCommentTags") : false;
    }
    get aiEvaluation() {
        return this.getProp("aiEvaluation");
    }
    get aiPrompt() {
        return this.getProp("aiPrompt");
    }
    get chatTutorPrompts() {
        return this.getProp("chatTutorPrompts");
    }
    get chatTutorProvider() {
        return this.getProp("chatTutorProvider");
    }
    get chatTutorEnabled() {
        return this.getProp("chatTutorEnabled");
    }
    get chatTutorIntro() {
        return this.getProp("chatTutorIntro");
    }
    get chatTutorHighlights() {
        return this.getProp("chatTutorHighlights");
    }
    get autoAssignStudentsToIndividualGroups() {
        return this.getProp("autoAssignStudentsToIndividualGroups");
    }
    get defaultDocumentType() {
        return this.getProp("defaultDocumentType");
    }
    get defaultDocumentTitle() {
        return this.getProp("defaultDocumentTitle");
    }
    get docTimeStampPropertyName() {
        return this.getProp("docTimeStampPropertyName");
    }
    get docDisplayIdPropertyName() {
        return this.getProp("docDisplayIdPropertyName");
    }
    get defaultLearningLogTitle() {
        return this.getProp("defaultLearningLogTitle");
    }
    get initialLearningLogTitle() {
        return this.getProp("initialLearningLogTitle");
    }
    get defaultLearningLogDocument() {
        return this.getProp("defaultLearningLogDocument");
    }
    get autoSectionProblemDocuments() {
        return this.getProp("autoSectionProblemDocuments");
    }
    get documentLabelProperties() {
        return this.getProp("documentLabelProperties");
    }
    get documentLabels() {
        return this.getProp("documentLabels");
    }
    get disablePublish() {
        return this.getProp("disablePublish");
    }
    get enableHistoryRoles() {
        return this.getProp("enableHistoryRoles");
    }
    get enableCommentRoles() {
        return this.getProp("enableCommentRoles");
    }
    get copyPreferOriginTitle() {
        return this.getProp("copyPreferOriginTitle");
    }
    get disableTileDrags() {
        return this.getProp("disableTileDrags");
    }
    get showClassSwitcher() {
        return this.getProp("showClassSwitcher");
    }
    get supportStackedTwoUpView() {
        return this.getProp("supportStackedTwoUpView");
    }
    get showPublishedDocsInPrimaryWorkspace() {
        return this.getProp("showPublishedDocsInPrimaryWorkspace");
    }
    get comparisonPlaceholderContent() {
        return this.getProp("comparisonPlaceholderContent");
    }
    get initiallyHideExemplars() {
        return this.getProp("initiallyHideExemplars");
    }
    get navTabs() {
        return this.getProp("navTabs");
    }
    /*
     * ProblemConfiguration properties
     */
    get disabledFeatures() {
        // settings are merged rather than simply returning the closest non-empty value
        const reverseConfigs = [...this.configs].reverse();
        const mergedDisabled = {};
        mergeDisabledFeatures(mergedDisabled, this.defaults.disabledFeatures);
        for (const config of reverseConfigs) {
            mergeDisabledFeatures(mergedDisabled, config.disabledFeatures);
        }
        return Object.values(mergedDisabled);
    }
    get toolbar() {
        return this.getProp("toolbar");
    }
    get annotations() {
        return this.getProp("annotations");
    }
    get authorTools() {
        return this.getProp("authorTools");
    }
    get myResourcesToolbar() {
        return this.getProp("myResourcesToolbar");
    }
    get tools() {
        return this.getProp("tools");
    }
    get defaultDocumentTemplate() {
        return this.getProp("defaultDocumentTemplate");
    }
    get defaultDocumentTemplateEnabled() {
        return this.getProp("defaultDocumentTemplateEnabled");
    }
    get planningTemplate() {
        return this.getProp("planningTemplate");
    }
    get planningTemplateEnabled() {
        return this.getProp("planningTemplateEnabled");
    }
    get placeholder() {
        return this.getProp("placeholder");
    }
    get placeholderText() {
        return this.getProp("placeholderText");
    }
    get stamps() {
        return this.getProp("stamps");
    }
    get showIdeasButton() {
        return this.getProp("showIdeasButton");
    }
    // Deliberately not implied by defaultDocumentType: "group" — a unit file states this itself so
    // anything reading the JSON (researcher reports) sees the same answer the app does. The authoring
    // form writes both keys. autoAssignStudentsToIndividualGroups (no real groups) overrides it.
    get groupDocumentsEnabled() {
        if (this.autoAssignStudentsToIndividualGroups)
            return false;
        return !!this.getProp("groupDocumentsEnabled");
    }
    // The warn-once flag is per ConfigurationManager instance: this getter is read on every render, so
    // an unguarded warn would spam the console for the whole session.
    get startsInGroupDocument() {
        const wantsGroup = this.defaultDocumentType === "group";
        if (wantsGroup && !this.groupDocumentsEnabled && !this.warnedGroupStartFallback) {
            this.warnedGroupStartFallback = true;
            const cause = this.autoAssignStudentsToIndividualGroups
                ? "autoAssignStudentsToIndividualGroups is set (no real groups)"
                : "groupDocumentsEnabled is not set";
            console.warn(`defaultDocumentType is "group" but ${cause}; ` +
                "students will start in the problem document instead");
        }
        return wantsGroup && this.groupDocumentsEnabled;
    }
    get hide4up() {
        return this.getProp("hide4up");
    }
    get sortWorkConfig() {
        return this.getProp("sortWorkConfig");
    }
    get termOverrides() {
        return this.getProp("termOverrides");
    }
    get defaultPanelLayout() {
        return this.getProp("defaultPanelLayout");
    }
    get contentLayout() {
        return this.getProp("contentLayout");
    }
    get fixedStartView() {
        return this.getProp("fixedStartView");
    }
    get fixedStartTab() {
        return this.getProp("fixedStartTab");
    }
    get defaultSharedDocuments() {
        return this.getProp("defaultSharedDocuments");
    }
    get showShare() {
        return this.getProp("showShare");
    }
    get classWideDocuments() {
        return this.getProp("classWideDocuments");
    }
    get settings() {
        // settings are merged rather than simply returning the closest non-empty value
        // merge is just two levels deep: eg merges items under settings.table, but not elements of settings.table.tools
        const reverseSettings = [...this.configs].reverse().map(config => config.settings);
        return assignWith({}, this.defaults.settings, ...reverseSettings, (objValue, srcValue) => {
            return assign({}, objValue, srcValue);
        });
    }
}
export function mergeDisabledFeatures(disabled, disabledFeatures) {
    disabledFeatures === null || disabledFeatures === void 0 ? void 0 : disabledFeatures.forEach(feature => {
        const result = /^!?(.+)/.exec(feature);
        const featureKey = result === null || result === void 0 ? void 0 : result[1];
        featureKey && (disabled[featureKey] = feature);
    });
}
//# sourceMappingURL=data:application/json;base64,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